"use client";

import { useMemo, useState } from "react";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import { List, ChevronDown } from "lucide-react";

function parseHeadings(
  html: string
): { id: string; text: string; level: number }[] {
  const headingRegex = /<h([23])(?:[^>]*)?>([^<]+)<\/h[23]>/gi;
  const headings: { id: string; text: string; level: number }[] = [];
  let match;

  while ((match = headingRegex.exec(html)) !== null) {
    const level = parseInt(match[1]);
    const text = match[2].trim();
    const id = text
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, "-")
      .replace(/(^-|-$)/g, "");
    headings.push({ id, text, level });
  }

  return headings;
}

export function TableOfContents({ content }: { content: string }) {
  const [isOpen, setIsOpen] = useState(false);
  const headings = useMemo(() => parseHeadings(content), [content]);

  if (headings.length < 3) return null;

  return (
    <Collapsible open={isOpen} onOpenChange={setIsOpen} className="mb-8">
      <Card className="border-[var(--roma-parchment-line)] bg-[var(--roma-linen)]/35 py-0 shadow-none">
        <CollapsibleTrigger className="w-full">
          <CardHeader className="px-5 py-4 flex flex-row items-center justify-between">
            <div className="flex items-center gap-2">
              <List className="h-4 w-4 text-[var(--roma-terracotta)]" />
              <span className="text-sm font-medium text-[var(--foreground)]">
                Table of Contents
              </span>
              <Badge
                variant="secondary"
                className="border border-[var(--roma-parchment-line)] bg-[var(--roma-ivory)] text-xs text-[var(--roma-terracotta)] shadow-none"
              >
                {headings.length}
              </Badge>
            </div>
            <ChevronDown
              className={`h-4 w-4 text-[var(--roma-terracotta)] transition-transform ${isOpen ? "rotate-180" : ""}`}
            />
          </CardHeader>
        </CollapsibleTrigger>
        <CollapsibleContent>
          <CardContent className="px-5 pb-4 pt-0">
            <nav className="space-y-1">
              {headings.map((heading, i) => (
                <a
                  key={i}
                  href={`#${heading.id}`}
                  onClick={(e) => {
                    e.preventDefault();
                    const el = document.getElementById(heading.id);
                    if (el) {
                      el.scrollIntoView({ behavior: "smooth", block: "start" });
                      setIsOpen(false);
                    }
                  }}
                  className={`block py-1.5 text-sm text-[var(--foreground)]/64 transition-colors hover:text-[var(--roma-terracotta)] ${
                    heading.level === 3
                      ? "border-l border-[var(--roma-parchment-line)] pl-4"
                      : "font-medium"
                  }`}
                >
                  {heading.text}
                </a>
              ))}
            </nav>
          </CardContent>
        </CollapsibleContent>
      </Card>
    </Collapsible>
  );
}
